export const STORY_WIDTH = 1080;
export const STORY_HEIGHT = 1920;

/** Keep story text above Instagram's bottom UI (reply bar, etc.). */
export const STORY_BOTTOM_SAFE_ZONE = 250;

export const STORY_COLORS = {
  yellow: "#FFEF57",
  black: "#000000",
  darkTop: "#141414",
  darkMid: "#3a3a14",
};

export const STORY_FIELD_DEFAULTS = {
  dayLabel: "MONDAYS",
  timeHour: "00",
  timePeriod: "AM",
  showTitle: "SHOW TITLE",
  hostPrefix: "HOST PREFIX",
  hostName: "HOST NAME",
} as const;

/** Raise host prefix slightly above curator name baseline (Figma). */
export const HOST_PREFIX_BASELINE_LIFT = 6;

export const STORY_WEEKDAY_OPTIONS = [
  "MONDAYS",
  "TUESDAYS",
  "WEDNESDAYS",
  "THURSDAYS",
  "FRIDAYS",
  "SATURDAYS",
  "SUNDAYS",
] as const;

export type StoryTimeOption = {
  hour: string;
  period: "AM" | "PM";
  label: string;
};

/** Show slots run every 2 hours (00, 02, 04, …). */
const STORY_SLOT_HOURS = ["00", "02", "04", "06", "08", "10"] as const;
const STORY_PM_SLOT_HOURS = ["12", "02", "04", "06", "08", "10"] as const;

export const STORY_TIME_OPTIONS: StoryTimeOption[] = [
  ...STORY_SLOT_HOURS.map((hour) => ({
    hour,
    period: "AM" as const,
    label: `${hour} AM`,
  })),
  ...STORY_PM_SLOT_HOURS.map((hour) => ({
    hour,
    period: "PM" as const,
    label: `${hour} PM`,
  })),
];

export const encodeStoryTimeValue = (hour: string, period: string): string =>
  `${hour}|${period}`;

export const decodeStoryTimeValue = (
  value: string
): { hour: string; period: "AM" | "PM" } => {
  const [
    hour = STORY_FIELD_DEFAULTS.timeHour,
    period = STORY_FIELD_DEFAULTS.timePeriod,
  ] = value.split("|");
  return {
    hour,
    period: period === "PM" ? "PM" : "AM",
  };
};

export const resolveStoryTimeValue = (hour: string, period: string): string => {
  const encoded = encodeStoryTimeValue(hour, period);
  const isValid = STORY_TIME_OPTIONS.some(
    (option) => encodeStoryTimeValue(option.hour, option.period) === encoded
  );

  return isValid
    ? encoded
    : encodeStoryTimeValue(
        STORY_FIELD_DEFAULTS.timeHour,
        STORY_FIELD_DEFAULTS.timePeriod
      );
};

/** Story v1 layout from storie v1.psd (1080×1920). */
export const STORY_V1 = {
  photoInset: 14,
  photoWidth: 1053,
  photoHeight: 1892,
  photoBorderRadius: 30,
  logo: { x: 59, y: 402, width: 186, height: 98 },
  type: { left: 60, top: 559 },
  weekDay: { offsetX: -2.65, offsetY: 0.5, fontSize: 53, lineHeight: 61 },
  hours: { offsetX: -1.53, offsetY: 81, fontSize: 90, lineHeight: 103 },
  show: {
    offsetX: 342,
    offsetY: 739,
    width: 612,
    fontSize: 139,
    lineHeight: 127,
  },
  curator: {
    offsetX: 342,
    offsetY: 1027,
    width: 612,
    fontSize: 44,
    lineHeight: 51,
    nameFontSize: 64,
    nameLineHeight: 74,
  },
};

/** Story v2 layout from storie v2.psd / Figma (1080×1920). */
export const STORY_V2 = {
  card: {
    x: 0,
    top: 0,
    width: STORY_WIDTH,
    photoPadding: 16,
  },
  photo: { y: 400, borderRadius: 30 },
  logo: { x: 850, y: 450, width: 186, height: 98 },
  type: { left: 60, top: 460 },
  weekDay: { offsetX: -2.65, offsetY: 0.5, fontSize: 53, lineHeight: 61 },
  hours: { offsetX: -2.06, offsetY: 81, fontSize: 82, lineHeight: 94 },
  show: {
    offsetX: 71.05,
    offsetY: 931.5,
    width: 895.9,
    fontSize: 119,
    lineHeight: 136,
  },
  curator: {
    offsetX: 71.05,
    offsetY: 1077,
    width: 895.9,
    fontSize: 44,
    lineHeight: 51,
    nameFontSize: 64,
    nameLineHeight: 74,
  },
};
